import { Injectable, NgZone } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { environment } from 'src/environments/environment';

/**
 * Route guard that blocks navigation to a route when the feature flag named in
 * the route's `data.featureFlag` is disabled for the current environment.
 *
 * This keeps the source code identical across branches: the routes and
 * components always exist, but are only reachable in environments where the
 * corresponding flag (see src/environments/environment*.ts) is enabled.
 *
 * Usage in routing:
 *   { path: 'AccessController', component: AccessControllerComponent,
 *     canActivate: [FeatureFlagGuard], data: { featureFlag: 'accountManagementEnabled' } }
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureFlagGuard implements CanActivate {
  constructor(private router: Router, private ngZone: NgZone) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): boolean | UrlTree {
    const flagName: string | undefined = route.data ? route.data['featureFlag'] : undefined;
    const flags: { [key: string]: boolean } = (environment as any).featureFlags || {};

    // No flag configured on the route means it is always allowed.
    if (!flagName || flags[flagName]) {
      return true;
    }

    // Feature is disabled for this environment: send the user to Access Denied.
    this.ngZone.run(() => this.router.navigate(['AccessDenied'])).then();
    return false;
  }
}
